import { useEffect, useState } from "react"
import { Head, Link } from "@inertiajs/react"
import {
  Coffee,
  Sparkles,
  Users,
  FileText,
  PieChart,
  BarChart3,
  TrendingUp,
  Bot,
  Heart,
  Target,
  Beaker,
  Award,
  Globe,
  ChevronDown,
  Clock,
  Brain,
  ArrowRight,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { signUpPath, dashboardPath } from "@/routes"
import { cn } from "@/lib/utils"
import { useInView } from "@/hooks/use-in-view"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { RadarChartExample } from "@/components/charts/RadarChartExample"
import { CoffeeFlavorWheel } from "@/components/charts/CoffeeFlavorWheel"
import {
  LanguageDetectionModal,
  LANGUAGE_NAMES,
} from "@/components/language-detection-modal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const SUPPORTED_LANGUAGES = [
  "es",
  "en",
  "zh-CN",
  "pt-BR",
  "it",
  "fr",
  "ja",
  "ko",
  "am",
]

function Animate({
  children,
  className,
  delay = 0,
  from = "bottom",
}: {
  children: React.ReactNode
  className?: string
  delay?: number
  from?: "bottom" | "left" | "right" | "none"
}) {
  const { ref, inView } = useInView()
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={cn(
        "transition-all duration-700 ease-out",
        inView ? "opacity-100" : "opacity-0",
        from === "bottom" && (inView ? "translate-y-0" : "translate-y-10"),
        from === "left" && (inView ? "translate-x-0" : "-translate-x-10"),
        from === "right" && (inView ? "translate-x-0" : "translate-x-10"),
        className,
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

export default function Welcome() {
  const { t, i18n } = useTranslation()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50)
    return () => clearTimeout(timer)
  }, [])

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem("i18nextLng", lang)
  }

  return (
    <>
      <Head title={`Catador Pro - ${t("home.hero.badge")}`} />

      <div className="bg-background min-h-screen">
        {/* Header */}
        <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 border-b backdrop-blur">
          <div className="container mx-auto flex h-16 items-center justify-between px-4">
            <div
              className={cn(
                "flex items-center transition-all duration-500",
                mounted
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-4 opacity-0",
              )}
            >
              <img src="/logo.png" alt="Catador Pro" className="h-24" />
            </div>
            <div
              className={cn(
                "flex items-center gap-3 transition-all delay-100 duration-500",
                mounted
                  ? "translate-x-0 opacity-100"
                  : "translate-x-4 opacity-0",
              )}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground gap-1.5"
                  >
                    <Globe className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {LANGUAGE_NAMES[i18n.language] || "English"}
                    </span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang}
                      onClick={() => handleLanguageChange(lang)}
                      className={
                        i18n.language === lang
                          ? "text-primary font-semibold"
                          : ""
                      }
                    >
                      {LANGUAGE_NAMES[lang]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link href={signUpPath()}>
                <Button className="bg-primary hover:bg-primary/90">
                  <Users className="mr-2 h-4 w-4" />
                  {t("home.nav.start")}
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
          {/* Background Image with subtle zoom-out on load */}
          <div
            className={cn(
              "absolute inset-0 bg-cover bg-center transition-transform duration-[2500ms] ease-out",
              mounted ? "scale-100" : "scale-110",
            )}
            style={{
              backgroundImage: `url('/images/hero-cupping-session.jpg')`,
            }}
          />
          {/* Overlay for text readability */}
          <div className="from-background/95 via-background/90 to-background/85 absolute inset-0 bg-gradient-to-br" />
          <div className="from-background absolute inset-0 bg-gradient-to-t via-transparent to-transparent" />

          <div className="relative container mx-auto px-4 py-16 md:py-24">
            <div className="mx-auto max-w-3xl text-center">
              {/* Badge — fade down */}
              <div
                className={cn(
                  "border-primary/20 bg-primary/5 text-primary mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-all duration-700",
                  mounted
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-4 opacity-0",
                )}
              >
                <Sparkles className="h-4 w-4" />
                <span>{t("home.hero.badge")}</span>
              </div>

              {/* Title — fade up, delay 150ms */}
              <h1
                className={cn(
                  "mb-6 text-4xl font-bold tracking-tight transition-all duration-700 md:text-6xl lg:text-7xl",
                  mounted
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0",
                )}
                style={{ transitionDelay: "150ms" }}
              >
                {t("home.hero.title")}
                <span className="text-primary">
                  {" "}
                  {t("home.hero.titleHighlight")}
                </span>
                {t("home.hero.titleEnd")}
              </h1>

              {/* Description — fade up, delay 300ms */}
              <p
                className={cn(
                  "text-muted-foreground mx-auto mb-12 max-w-2xl text-lg transition-all duration-700 md:text-xl",
                  mounted
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0",
                )}
                style={{ transitionDelay: "300ms" }}
              >
                {t("home.hero.description")}
              </p>
            </div>

            {/* Features Highlight — staggered fade up */}
            <div className="mx-auto mt-16 grid max-w-4xl gap-8 md:grid-cols-3">
              {[
                {
                  value: t("home.features.collaborative.title"),
                  label: t("home.features.collaborative.label"),
                  desc: t("home.features.collaborative.desc"),
                  delay: 450,
                },
                {
                  value: t("home.features.ai.title"),
                  label: t("home.features.ai.label"),
                  desc: t("home.features.ai.desc"),
                  delay: 550,
                },
                {
                  value: t("home.features.share.title"),
                  label: t("home.features.share.label"),
                  desc: t("home.features.share.desc"),
                  delay: 650,
                },
              ].map((stat) => (
                <div
                  key={stat.value}
                  className={cn(
                    "text-center transition-all duration-700",
                    mounted
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0",
                  )}
                  style={{ transitionDelay: `${stat.delay}ms` }}
                >
                  <div className="text-primary mb-1 font-serif text-3xl font-bold">
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    {stat.label}
                    {stat.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Protocols Section */}
        <section className="border-border/50 bg-muted relative overflow-hidden border-t py-20">
          <div
            className="absolute top-0 right-0 h-full w-1/3 opacity-5"
            style={{
              backgroundImage: `url('/images/coffee-beans-detail.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative container mx-auto px-4">
            <Animate className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
                {t("home.protocols.title")}{" "}
                <span className="text-primary">
                  {t("home.protocols.titleHighlight")}
                </span>
              </h2>
              <p className="text-muted-foreground text-lg">
                {t("home.protocols.description")}
              </p>
            </Animate>

            <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
              {[
                {
                  color: "primary" as const,
                  icon: <Beaker className="text-primary h-5 w-5" />,
                  title: t("home.protocols.sca_arabica.title"),
                  subtitle: t("home.protocols.sca_arabica.subtitle"),
                  desc: t("home.protocols.sca_arabica.desc"),
                  delay: 0,
                },
                {
                  color: "primary" as const,
                  icon: <Beaker className="text-primary h-5 w-5" />,
                  title: t("home.protocols.sca_robusta.title"),
                  subtitle: t("home.protocols.sca_robusta.subtitle"),
                  desc: t("home.protocols.sca_robusta.desc"),
                  delay: 75,
                },
                {
                  color: "primary" as const,
                  icon: <Award className="text-primary h-5 w-5" />,
                  title: t("home.protocols.coe.title"),
                  subtitle: t("home.protocols.coe.subtitle"),
                  desc: t("home.protocols.coe.desc"),
                  delay: 150,
                },
                {
                  color: "amber" as const,
                  icon: <Heart className="h-5 w-5 text-amber-600" />,
                  title: t("home.protocols.cva_affective.title"),
                  subtitle: t("home.protocols.cva_affective.subtitle"),
                  desc: t("home.protocols.cva_affective.desc"),
                  delay: 225,
                },
                {
                  color: "amber" as const,
                  icon: <Target className="h-5 w-5 text-amber-600" />,
                  title: t("home.protocols.cva_descriptive.title"),
                  subtitle: t("home.protocols.cva_descriptive.subtitle"),
                  desc: t("home.protocols.cva_descriptive.desc"),
                  delay: 300,
                },
                {
                  color: "amber" as const,
                  icon: <BarChart3 className="h-5 w-5 text-amber-600" />,
                  title: t("home.protocols.cva_combined.title"),
                  subtitle: t("home.protocols.cva_combined.subtitle"),
                  desc: t("home.protocols.cva_combined.desc"),
                  delay: 375,
                },
              ].map((card) => (
                <Animate key={card.title} delay={card.delay}>
                  <Card
                    className={cn(
                      "bg-card relative h-full overflow-hidden border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg",
                      card.color === "primary"
                        ? "border-primary/20"
                        : "border-amber-500/20",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute top-0 right-0 left-0 h-1 bg-gradient-to-r",
                        card.color === "primary"
                          ? "from-primary to-primary/50"
                          : "from-amber-500 to-amber-500/50",
                      )}
                    />
                    <CardContent className="p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <div
                          className={cn(
                            "flex h-10 w-10 items-center justify-center rounded-xl",
                            card.color === "primary"
                              ? "bg-primary/10"
                              : "bg-amber-500/10",
                          )}
                        >
                          {card.icon}
                        </div>
                        <div>
                          <h3 className="font-serif text-lg font-semibold">
                            {card.title}
                          </h3>
                          <p className="text-muted-foreground text-xs">
                            {card.subtitle}
                          </p>
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {card.desc}
                      </p>
                    </CardContent>
                  </Card>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        {/* Workspace Section */}
        <section className="bg-background relative py-20">
          <div className="container mx-auto px-4">
            <Animate
              from="none"
              className="mx-auto mb-12 max-w-4xl overflow-hidden rounded-2xl shadow-2xl"
            >
              <img
                src="/images/workspace-digital-cupping.jpg"
                alt={t("home.images.workspace_alt")}
                className="h-auto w-full object-cover"
                loading="lazy"
              />
            </Animate>

            <Animate className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
                {t("home.workspace.title")}{" "}
                <span className="text-primary">
                  {t("home.workspace.titleHighlight")}
                </span>
              </h2>
              <p className="text-muted-foreground text-lg">
                {t("home.workspace.description")}
              </p>
            </Animate>

            <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-3">
              {[
                {
                  img: "/images/team-collaboration.jpg",
                  alt: t("home.images.team_alt"),
                  icon: <Users className="text-primary h-7 w-7" />,
                  title: t("home.workspace.team.title"),
                  desc: t("home.workspace.team.desc"),
                  delay: 0,
                },
                {
                  img: "/images/coffee-tracking-database.jpg",
                  alt: t("home.images.tracking_alt"),
                  icon: <Coffee className="text-primary h-7 w-7" />,
                  title: t("home.workspace.tracking.title"),
                  desc: t("home.workspace.tracking.desc"),
                  delay: 150,
                },
                {
                  img: "/images/ai-assistant-client-sharing.jpg",
                  alt: t("home.images.ai_alt"),
                  icon: <Bot className="text-primary h-7 w-7" />,
                  title: t("home.workspace.clients.title"),
                  desc: t("home.workspace.clients.desc"),
                  delay: 300,
                },
              ].map((card) => (
                <Animate key={card.title} delay={card.delay}>
                  <Card className="border-border/50 h-full overflow-hidden border p-0 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={card.img}
                        alt={card.alt}
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <CardContent className="mt-0 p-8 text-center">
                      <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
                        {card.icon}
                      </div>
                      <h3 className="mb-3 font-serif text-xl font-semibold">
                        {card.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {card.desc}
                      </p>
                    </CardContent>
                  </Card>
                </Animate>
              ))}
            </div>
          </div>
        </section>

        {/* Reports Section */}
        <section className="border-border/50 bg-muted relative border-t py-20">
          <div className="container mx-auto px-4">
            <Animate className="mx-auto mb-12 max-w-3xl text-center">
              <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-4xl">
                {t("home.reports.title")}{" "}
                <span className="text-primary">
                  {t("home.reports.titleHighlight")}
                </span>
              </h2>
              <p className="text-muted-foreground text-lg">
                {t("home.reports.description")}
              </p>
            </Animate>

            <Animate
              from="none"
              className="mx-auto mb-12 max-w-4xl overflow-hidden rounded-2xl shadow-2xl"
            >
              <img
                src="/images/reports-tablet-view.jpg"
                alt={t("home.images.reports_alt")}
                className="h-auto w-full object-cover"
                loading="lazy"
              />
            </Animate>

            <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-2">
              {/* Report Features — slide in from left */}
              <div className="space-y-6">
                {[
                  {
                    icon: <PieChart className="text-primary h-5 w-5" />,
                    title: t("home.reports.sensory_profile.title"),
                    desc: t("home.reports.sensory_profile.desc"),
                    delay: 0,
                  },
                  {
                    icon: <BarChart3 className="text-primary h-5 w-5" />,
                    title: t("home.reports.score_breakdown.title"),
                    desc: t("home.reports.score_breakdown.desc"),
                    delay: 100,
                  },
                  {
                    icon: <TrendingUp className="text-primary h-5 w-5" />,
                    title: t("home.reports.flavors.title"),
                    desc: t("home.reports.flavors.desc"),
                    delay: 200,
                  },
                  {
                    icon: <Users className="text-primary h-5 w-5" />,
                    title: t("home.reports.results.title"),
                    desc: t("home.reports.results.desc"),
                    delay: 300,
                  },
                  {
                    icon: <Bot className="text-primary h-5 w-5" />,
                    title: t("home.reports.ai_assistant.title"),
                    desc: t("home.reports.ai_assistant.desc"),
                    delay: 400,
                  },
                  {
                    icon: <FileText className="text-primary h-5 w-5" />,
                    title: t("home.reports.export.title"),
                    desc: t("home.reports.export.desc"),
                    delay: 500,
                  },
                ].map((item) => (
                  <Animate key={item.title} from="left" delay={item.delay}>
                    <Card className="border-border/50 hover:border-primary/30 border transition-all duration-300 hover:shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                            {item.icon}
                          </div>
                          <div>
                            <h4 className="mb-2 font-semibold">{item.title}</h4>
                            <p className="text-muted-foreground text-sm">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Animate>
                ))}
              </div>

              {/* Visualization Cards — slide in from right */}
              <div className="space-y-6">
                <Animate from="right" delay={100}>
                  <Card className="border-border/50 border-2">
                    <CardContent className="p-6">
                      <h4 className="text-foreground mb-4 text-center font-semibold">
                        {t("home.reports.flavor_wheel.title")}
                      </h4>
                      <div className="h-[400px] w-full">
                        <CoffeeFlavorWheel />
                      </div>
                      <p className="text-muted-foreground mt-4 text-center text-sm">
                        {t("home.reports.flavor_wheel.subtitle")}
                      </p>
                    </CardContent>
                  </Card>
                </Animate>

                <Animate from="right" delay={250}>
                  <Card className="border-border/50 border-2">
                    <CardContent className="p-6">
                      <h4 className="text-foreground mb-4 text-center font-semibold">
                        {t("home.reports.sensory_example.title")}
                      </h4>
                      <RadarChartExample />
                      <p className="text-muted-foreground mt-4 text-center text-sm">
                        {t("home.reports.sensory_example.subtitle")}
                      </p>
                    </CardContent>
                  </Card>
                </Animate>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="border-border/50 relative overflow-hidden border-t">
          {/* Gradient background */}
          <div className="from-primary/10 via-background to-primary/5 absolute inset-0 bg-gradient-to-br" />
          <div className="from-primary/8 absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] via-transparent to-transparent" />

          <div className="relative container mx-auto px-4 py-20">
            <Animate>
              {/* Badge */}
              <div className="mb-6 flex justify-center">
                <span className="border-primary/30 bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium">
                  <Sparkles className="h-3.5 w-3.5" />
                  AI-Powered
                </span>
              </div>

              {/* Heading */}
              <h2 className="text-foreground mx-auto mb-4 max-w-2xl text-center text-3xl font-bold md:text-4xl">
                {t("home.cta.title")}
              </h2>
              <p className="text-muted-foreground mx-auto mb-12 max-w-xl text-center">
                {t("home.cta.description")}
              </p>

              {/* Value props */}
              <div className="mx-auto mb-12 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="border-border/60 bg-card/60 flex flex-col items-center gap-3 rounded-xl border p-5 text-center backdrop-blur-sm">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-full">
                    <Clock className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground text-sm font-semibold">
                    {t("home.cta.value1_title")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("home.cta.value1_desc")}
                  </p>
                </div>
                <div className="border-border/60 bg-card/60 flex flex-col items-center gap-3 rounded-xl border p-5 text-center backdrop-blur-sm">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-full">
                    <TrendingUp className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground text-sm font-semibold">
                    {t("home.cta.value2_title")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("home.cta.value2_desc")}
                  </p>
                </div>
                <div className="border-border/60 bg-card/60 flex flex-col items-center gap-3 rounded-xl border p-5 text-center backdrop-blur-sm">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-full">
                    <Brain className="text-primary h-5 w-5" />
                  </div>
                  <p className="text-foreground text-sm font-semibold">
                    {t("home.cta.value3_title")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("home.cta.value3_desc")}
                  </p>
                </div>
              </div>

              {/* CTA button */}
              <div className="flex justify-center">
                <Link href={dashboardPath()}>
                  <Button
                    size="lg"
                    className="group hover:shadow-primary/20 gap-2 px-8 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  >
                    <Coffee className="h-5 w-5" />
                    {t("home.cta.button")}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </div>
            </Animate>
          </div>
        </section>
      </div>
      <LanguageDetectionModal guestMode />
    </>
  )
}
